<template>
	<el-dialog :visible="visible" width="720px" class="chat-rich-composer" append-to-body
		:close-on-click-modal="false" :close-on-press-escape="!sending" :show-close="!sending" @close="$emit('close')">
		<div class="composer-body" v-loading="sending">
			<div class="composer-toolbar">
				<el-button size="mini" icon="el-icon-picture-outline" :disabled="sending"
					@click="triggerMediaInput('image')">
					{{ $t('chat.sendImage') }}
				</el-button>
				<el-button size="mini" icon="el-icon-film" :disabled="sending" @click="triggerMediaInput('video')">
					{{ $t('chat.sendVideo') }}
				</el-button>
				<div class="toolbar-tip">{{ mediaCount }}/9</div>
			</div>
			<chat-rich-media-album v-if="mediaList.length" :items="previewItems" :maxWidth="420" :rounded="false"
				:showVideoControls="false">
			</chat-rich-media-album>
			<div v-if="mediaList.length" class="media-chip-list">
				<div v-for="item in mediaList" :key="item.id" class="media-chip" :class="mediaChipClass(item)">
					<span>{{ item.kind === 'video' ? $t('chat.previewVideo') : $t('chat.previewImage') }}</span>
					<span class="media-chip-state" v-if="item.uploadStatus === 'uploading'">{{ $t('common.uploading') }}</span>
					<span class="media-chip-state" v-else-if="item.uploadStatus === 'failed'">{{ $t('chat.uploadFailed') }}</span>
					<i class="el-icon-close" :class="{ disabled: sending }" @click="onRemoveMedia(item.id)"></i>
				</div>
			</div>
			<div class="composer-input">
				<chat-input ref="editor" :group="group" :group-members="groupMembers" :submit-on-enter="false"
					:allow-media-paste="false">
				</chat-input>
			</div>
		</div>
		<span slot="footer" class="dialog-footer">
			<el-button :disabled="sending" @click="$emit('close')">{{ $t('common.cancel') }}</el-button>
			<el-button type="primary" :loading="sending" :disabled="!mediaList.length || sending"
				@click="onConfirm">{{ $t('common.send') }}</el-button>
		</span>
		<input ref="imageInput" class="hidden-input" type="file" accept="image/jpeg,image/png,image/jpg,image/webp,image/gif"
			multiple @change="onMediaSelected($event, 'image')" />
		<input ref="videoInput" class="hidden-input" type="file" accept="video/mp4,video/ogg,video/webm"
			multiple @change="onMediaSelected($event, 'video')" />
	</el-dialog>
</template>

<script>
import ChatInput from "./ChatInput";
import ChatRichMediaAlbum from "./ChatRichMediaAlbum.vue";
import { MESSAGE_TYPE } from "../../api/enums";
import { buildSingleMediaContent, createRichContent, deriveAtUserIds, trimRichTextBlocks, segmentsToPlainText } from "../../utils/richMessage";
import { setCachedMediaSize } from "../../utils/mediaSize";

export default {
	name: "ChatRichComposerDialog",
	components: {
		ChatInput,
		ChatRichMediaAlbum
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		group: {
			type: Object
		},
		groupMembers: {
			type: Array,
			default: () => []
		},
		sending: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			mediaList: [],
			nextMediaId: 1
		};
	},
	computed: {
		mediaCount() {
			return this.mediaList.length;
		},
		previewItems() {
			return this.mediaList.map((item) => ({
				...item.previewItem,
				uploadStatus: item.uploadStatus,
				uploadError: item.uploadError
			}));
		},
		uploadingCount() {
			return this.mediaList.filter((item) => item.uploadStatus === 'uploading').length;
		},
		failedCount() {
			return this.mediaList.filter((item) => item.uploadStatus === 'failed').length;
		},
		successMediaList() {
			return this.mediaList.filter((item) => item.uploadStatus === 'success' && item.uploadedItem);
		}
	},
	watch: {
		visible(value) {
			if (value) {
				this.$nextTick(() => {
					this.$refs.editor && this.$refs.editor.focus();
				});
			}
		}
	},
	methods: {
		reset() {
			this.mediaList.forEach((item) => {
				if (item.previewUrl) {
					URL.revokeObjectURL(item.previewUrl);
				}
			});
			this.mediaList = [];
			this.nextMediaId = 1;
			this.$refs.editor && this.$refs.editor.clear();
		},
		triggerMediaInput(kind) {
			if (this.sending) {
				return;
			}
			const input = kind === 'video' ? this.$refs.videoInput : this.$refs.imageInput;
			if (input) {
				input.value = '';
				input.click();
			}
		},
		onMediaSelected(event, kind) {
			const files = Array.from(event.target.files || []);
			files.forEach((file) => this.appendMedia(file, kind));
			event.target.value = '';
		},
		appendMedia(file, kind) {
			if (this.mediaList.length >= 9) {
				this.$message.warning('最多只能添加9个媒体');
				return;
			}
			const isVideo = kind === 'video';
			const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
			if (file.size > maxSize) {
				this.$message.error(this.$t('common.fileSizeExceed', { size: isVideo ? '50M' : '10M' }));
				return;
			}
			const previewUrl = URL.createObjectURL(file);
			const previewItem = isVideo ? {
				kind: 'video',
				videoUrl: previewUrl,
				coverUrl: ''
			} : {
				kind: 'image',
				originUrl: previewUrl,
				thumbUrl: previewUrl
			};
			const item = {
				id: this.nextMediaId++,
				kind,
				file,
				previewUrl,
				previewItem,
				uploadStatus: 'uploading',
				uploadError: '',
				uploadedItem: null
			};
			this.mediaList.push(item);
			this.probeLocalSize(item);
			this.uploadMedia(item);
		},
		probeLocalSize(item) {
			if (item.kind === 'image') {
				const image = new Image();
				image.onload = () => {
					setCachedMediaSize(item.previewItem, {
						width: image.naturalWidth || image.width,
						height: image.naturalHeight || image.height
					});
				};
				image.src = item.previewUrl;
				return;
			}
			const video = document.createElement('video');
			video.preload = 'metadata';
			video.onloadedmetadata = () => {
				setCachedMediaSize(item.previewItem, {
					width: video.videoWidth,
					height: video.videoHeight
				});
			};
			video.src = item.previewUrl;
		},
		uploadMedia(item) {
			const requestToken = Symbol(`upload-${item.id}`);
			item.uploadToken = requestToken;
			item.uploadStatus = 'uploading';
			item.uploadError = '';
			item.uploadedItem = null;
			const formData = new FormData();
			formData.append('file', item.file);
			const action = item.kind === 'video' ? "/video/upload" : "/image/upload?isPermanent=false";
			this.$http.post(action, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			}).then((data) => {
				const current = this.findMediaItem(item.id);
				if (!current || current.uploadToken !== requestToken) {
					return;
				}
				current.uploadedItem = this.normalizeUploadedItem(current.kind, data);
				current.uploadStatus = 'success';
				current.uploadError = '';
			}).catch(() => {
				const current = this.findMediaItem(item.id);
				if (!current || current.uploadToken !== requestToken) {
					return;
				}
				current.uploadStatus = 'failed';
				current.uploadError = this.$t('chat.uploadFailed');
				current.uploadedItem = null;
			});
		},
		normalizeUploadedItem(kind, data) {
			const safeData = { ...(data || {}) };
			delete safeData.aspectRatio;
			if (kind === 'video') {
				return {
					kind: 'video',
					videoUrl: safeData.videoUrl,
					coverUrl: safeData.coverUrl || '',
					width: Number(safeData.width) || undefined,
					height: Number(safeData.height) || undefined
				};
			}
			return {
				kind: 'image',
				originUrl: safeData.originUrl,
				thumbUrl: safeData.thumbUrl || safeData.originUrl,
				width: Number(safeData.width) || undefined,
				height: Number(safeData.height) || undefined
			};
		},
		findMediaItem(id) {
			return this.mediaList.find((item) => item.id === id);
		},
		mediaChipClass(item) {
			return item && item.uploadStatus ? `is-${item.uploadStatus}` : '';
		},
		onRemoveMedia(id) {
			if (this.sending) {
				return;
			}
			this.removeMedia(id);
		},
		removeMedia(id) {
			const index = this.mediaList.findIndex((item) => item.id === id);
			if (index < 0) {
				return;
			}
			const [removed] = this.mediaList.splice(index, 1);
			if (removed && removed.previewUrl) {
				URL.revokeObjectURL(removed.previewUrl);
			}
		},
		onConfirm() {
			if (this.uploadingCount > 0) {
				this.$message.warning(this.$t('chat.richMediaWaitUpload'));
				return;
			}
			if (!this.successMediaList.length) {
				this.$message.warning(this.$t('chat.richMediaNoUploaded'));
				return;
			}
			if (this.failedCount > 0) {
				this.$message.warning(this.$t('chat.richMediaUploadFailed'));
				return;
			}
			const segments = this.$refs.editor ? this.$refs.editor.buildTextSegments() : [];
			const blocks = [];
			const text = segmentsToPlainText(segments).trim();
			const uploadedItems = this.successMediaList.map((item) => ({ ...item.uploadedItem }));
			blocks.push({
				type: 'media',
				items: uploadedItems
			});
			if (text) {
				blocks.push({
					type: 'text',
					segments
				});
			}
			const trimmedBlocks = trimRichTextBlocks(blocks);
			if (uploadedItems.length === 1 && !text) {
				this.$emit('submit', {
					type: uploadedItems[0].kind === 'video' ? MESSAGE_TYPE.VIDEO : MESSAGE_TYPE.IMAGE,
					content: buildSingleMediaContent(uploadedItems[0]),
					atUserIds: []
				});
				return;
			}
			this.$emit('submit', {
				type: MESSAGE_TYPE.RICH_TEXT,
				content: JSON.stringify(createRichContent(trimmedBlocks)),
				atUserIds: deriveAtUserIds(trimmedBlocks)
			});
		}
	}
}
</script>

<style scoped lang="scss">
.chat-rich-composer {
	.hidden-input {
		display: none;
	}

	.composer-body {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.composer-toolbar {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.toolbar-tip {
		margin-left: auto;
		color: var(--im-text-color-light);
		font-size: var(--im-font-size-small);
	}

	.media-chip-list {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.media-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 10px;
		background: var(--im-background-active);
		border-radius: 999px;
		font-size: var(--im-font-size-small);
		color: var(--im-text-color-light);

		&.is-uploading {
			background: rgba(64, 158, 255, 0.1);
			color: var(--im-color-primary);
		}

		&.is-failed {
			background: rgba(245, 108, 108, 0.12);
			color: var(--im-color-danger);
		}

		.media-chip-state {
			font-size: 12px;
		}

		i {
			cursor: pointer;

			&.disabled {
				cursor: not-allowed;
				opacity: 0.45;
			}
		}
	}

	.composer-input {
		height: 180px;
		border: 1px solid #dcdfe6;
		border-radius: 10px;
		padding: 8px;
		box-sizing: border-box;
	}
}
</style>
